import { useState } from "react";
import { MapPin } from "lucide-react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card } from "../../components/shared/Card";
import { Badge } from "../../components/shared/Badge";
import { useAppData } from "../../lib/AppDataContext";
import { ATTENDANCE_STATUS_META, formatDurationMs, withAutoAbsent } from "../../lib/attendance";
import { MonthPicker } from "../../components/shared/MonthPicker";

/* History — last 90 days for a chosen sales person. Full audit columns:
   selfies in + out, locations in + out, total hours, reason, marked-by. */
export function AttendanceHistoryView() {
  const { allSalesPeople, attendanceLog, officeTiming, month, setMonth } = useAppData();
  // Exclude Settings>Users-deactivated users and super_admins.
  const activeSalesPeople = allSalesPeople.filter(
    (m) => m.userActive !== false && m.role !== "super_admin"
  );
  const [who, setWho] = useState(activeSalesPeople[0]?.name || "");
  const [preview, setPreview] = useState(null);
  // Scoped to the selected month so auto-absent days can be filled in for
  // exactly that period (see withAutoAbsent).
  const monthPrefix = (() => {
    const [mn, yr] = String(month).split(" ");
    const idx = ["January","February","March","April","May","June","July","August","September","October","November","December"].indexOf(mn);
    return idx < 0 ? null : `${yr}-${String(idx + 1).padStart(2, "0")}`;
  })();
  const log = (attendanceLog[who] || []).filter((r) => !monthPrefix || r.date.startsWith(monthPrefix));
  // `who` is a name, so the employee has to be resolved to reach joiningDate.
  const whoJoined = allSalesPeople.find((p) => p.name === who)?.profile?.joiningDate;
  const recent = withAutoAbsent(log, officeTiming?.weekendDay ?? 0, officeTiming?.endHHMM, month, whoJoined)
    .sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div>
      {preview && (
        <div className="fixed inset-0 z-[60] bg-slate-900/80 grid place-items-center p-6" onClick={() => setPreview(null)}>
          <img src={preview} alt="Selfie preview" className="max-w-[90vw] max-h-[85vh] rounded-lg ring-1 ring-white/20" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
      <PageHeader accent="ops" title="Attendance · History" subtitle="Per employee, by month" />
      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="w-full sm:w-72">
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Employees</label>
            <select value={who} onChange={(e) => setWho(e.target.value)}
              className="text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500 w-full">
              {activeSalesPeople.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Month</label>
            <MonthPicker value={month} onChange={setMonth} />
          </div>
        </div>
      </Card>
      <Card>
        {recent.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No history recorded for {who}.</div>
        ) : (
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="sticky top-0 z-20 text-left text-xs font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Clock-in</th>
                  <th className="px-3 py-2.5">In location</th>
                  <th className="px-3 py-2.5">In selfie</th>
                  <th className="px-3 py-2.5">Clock-out</th>
                  <th className="px-3 py-2.5">Out location</th>
                  <th className="px-3 py-2.5">Out selfie</th>
                  <th className="px-3 py-2.5">Total hours</th>
                  <th className="px-3 py-2.5">Reason / note</th>
                  <th className="px-3 py-2.5">Marked by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recent.map((r) => {
                  const meta = ATTENDANCE_STATUS_META[r.status];
                  const total = r.clockInAt && r.clockOutAt
                    ? formatDurationMs(new Date(r.clockOutAt) - new Date(r.clockInAt)) : null;
                  const inSelfie = r.clockInSelfie || r.selfie;
                  const inLocation = r.clockInLocation || r.location;
                  const dateObj = new Date(r.date);
                  const weekday = dateObj.toLocaleDateString("en-IN", { weekday: "short" });
                  const dateStr = dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                  return (
                    <tr key={r.date} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5 tabular-nums text-slate-600">
                        <div className="font-medium text-slate-900">{dateStr}</div>
                        <div className="text-[10px] text-slate-400">{weekday}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded ring-1 ring-inset ${meta.cls}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />{meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-slate-600">
                        {r.clockInTime || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">
                        {inLocation ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <MapPin className="w-3 h-3 text-slate-400" />{inLocation}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        {inSelfie ? (
                          <img src={inSelfie} alt="In selfie" onClick={() => setPreview(inSelfie)} className="w-8 h-8 rounded object-cover ring-1 ring-slate-200 cursor-pointer hover:ring-blue-400" />
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-slate-600">
                        {r.clockOutTime || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">
                        {r.clockOutLocation ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <MapPin className="w-3 h-3 text-slate-400" />{r.clockOutLocation}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        {r.clockOutSelfie ? (
                          <img src={r.clockOutSelfie} alt="Out selfie" onClick={() => setPreview(r.clockOutSelfie)} className="w-8 h-8 rounded object-cover ring-1 ring-slate-200 cursor-pointer hover:ring-blue-400" />
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">
                        {total ? <span className="font-semibold text-blue-700">{total}</span> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-500 max-w-[200px] truncate" title={r.reason || ""}>
                        {r.reason || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge cls={r.autoAbsent
                          ? "bg-slate-50 text-slate-500 ring-slate-300/40"
                          : r.markedBy === "self"
                            ? "bg-slate-100 text-slate-600 ring-slate-400/20"
                            : "bg-blue-50 text-blue-700 ring-blue-600/20"}>
                          {r.autoAbsent ? "Auto" : r.markedBy === "self" ? "Self" : "Admin"}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
